import { useEffect, useCallback, useRef } from 'react';
import { db } from '../firebase-config';
import { doc, getDoc, setDoc } from 'firebase/firestore';

const GLOBAL_COUNTER_PATH = 'global/counter';

export default function useFirestoreListeners({
  gameStarted,
  isOnline,
  playerId,
  isGuest,
  setDisplayCount,
  setGlobalCount,
  setLocalGlobalIncrement,
  setIsLoading,
  setError,
  setOverallMaxCps,
  setOverallMaxCombo,
  setDailyClicks,
  setCurrentStreak,
  setLongestStreak,
  setStreakFreezes,
  setSoundEnabled,
  setWeeklyAchievementData,
}) {
  const lastGlobalCountRef = useRef(0);

  const getUserDocPath = useCallback(() => {
    if (!playerId) return null;
    return isGuest ? doc(db, 'guests', playerId) : doc(db, 'users', playerId);
  }, [playerId, isGuest]);

  useEffect(() => {
    if (!gameStarted || !isOnline || !playerId) return;

    const globalDoc = doc(db, GLOBAL_COUNTER_PATH);

    setIsLoading(true);
    setError(null);

    // For guests, only load global counter once
    if (isGuest) {
      const initializeGlobalCounter = async (retryCount = 0) => {
        try {
          const globalDocSnap = await getDoc(globalDoc);
          if (globalDocSnap.exists()) {
            const initialCount = globalDocSnap.data().value || 0;
            setGlobalCount(initialCount);
            lastGlobalCountRef.current = initialCount;
            setLocalGlobalIncrement(0);
          } else {
            await setDoc(globalDoc, { value: 0 });
          }
          setError(null);
          setIsLoading(false);
        } catch (initError) {
          console.error('Error initializing global counter:', initError);
          if (initError.code === 'permission-denied' && retryCount < 3) {
            console.log(`Retrying initialization, attempt ${retryCount + 1}/3`);
            setTimeout(() => {
              initializeGlobalCounter(retryCount + 1);
            }, 1000 * (retryCount + 1));
          } else {
            if (initError.code === 'permission-denied') {
              setError('Not authenticated. Please refresh the page.');
            } else {
              setError('Could not load global score.');
            }
            setIsLoading(false);
          }
        }
      };

      initializeGlobalCounter();
      return;
    }

    // For named users, do a single init read for user + global + load all startup data
    const userDoc = getUserDocPath();

    const initializeCounters = async (retryCount = 0) => {
      try {
        const [userDocSnap, globalDocSnap] = await Promise.all([
          getDoc(userDoc),
          getDoc(globalDoc),
        ]);

        if (userDocSnap.exists()) {
          const userData = userDocSnap.data();
          setDisplayCount(userData.value || 0);

          // Load overall max values for comparison
          if (setOverallMaxCps) setOverallMaxCps(userData.maxCps || 0);
          if (setOverallMaxCombo) setOverallMaxCombo(userData.maxCombo || 0);

          // Load daily tracking data
          if (setDailyClicks) setDailyClicks(userData.dailyClicks || 0);
          if (setLongestStreak) setLongestStreak(userData.longestStreak || 0);
          if (setStreakFreezes) setStreakFreezes(userData.streakFreezes || 0);

          // Check if streak is still valid based on lastActiveDate
          // Streak is stale if user hasn't played today or yesterday (AOE time)
          if (setCurrentStreak) {
            const storedStreak = userData.currentStreak || 0;
            if (storedStreak > 0 && userData.lastActiveDate) {
              const toAoeDayKey = (d) => {
                const aoe = new Date(d.getTime() - 12 * 60 * 60 * 1000);
                return aoe.toISOString().split('T')[0];
              };
              const now = new Date();
              const todayKey = toAoeDayKey(now);
              const yesterdayKey = toAoeDayKey(new Date(now.getTime() - 24 * 60 * 60 * 1000));
              const lastDate = userData.lastActiveDate.toDate ? userData.lastActiveDate.toDate() : new Date(userData.lastActiveDate);
              const lastKey = toAoeDayKey(lastDate);
              setCurrentStreak(lastKey === todayKey || lastKey === yesterdayKey ? storedStreak : 0);
            } else {
              setCurrentStreak(storedStreak);
            }
          }

          // Load sound preference (#4/#9 consolidated — trust localStorage after this)
          if (setSoundEnabled && userData.hasOwnProperty('soundEnabled')) {
            setSoundEnabled(userData.soundEnabled);
          }

          // Load weekly achievement data (#4 consolidated)
          if (setWeeklyAchievementData) {
            setWeeklyAchievementData({
              weeklyTop10s: userData.weeklyTop10s || [],
              pendingAchievements: userData.pendingAchievements || [],
              weeklyParticipation: userData.weeklyParticipation || [],
              pendingWeeklyParticipation: userData.pendingWeeklyParticipation || [],
            });
          }
        } else {
          await setDoc(userDoc, {
            value: 0,
            maxCombo: 0,
            maxCps: 0,
            dailyClicks: 0,
            currentStreak: 0,
            longestStreak: 0,
            streakFreezes: 0
          });
          if (setOverallMaxCps) setOverallMaxCps(0);
          if (setOverallMaxCombo) setOverallMaxCombo(0);
          if (setDailyClicks) setDailyClicks(0);
          if (setCurrentStreak) setCurrentStreak(0);
          if (setLongestStreak) setLongestStreak(0);
          if (setStreakFreezes) setStreakFreezes(0);
        }

        if (globalDocSnap.exists()) {
          const initialCount = globalDocSnap.data().value || 0;
          setGlobalCount(initialCount);
          lastGlobalCountRef.current = initialCount;
          setLocalGlobalIncrement(0);
        } else {
          await setDoc(globalDoc, { value: 0 });
        }
        setError(null);
        setIsLoading(false);
      } catch (initError) {
        console.error('Error initializing counters:', initError);
        if (initError.code === 'permission-denied' && retryCount < 3) {
          console.log(`Retrying initialization for named user, attempt ${retryCount + 1}/3`);
          setTimeout(() => {
            initializeCounters(retryCount + 1);
          }, 1000 * (retryCount + 1));
        } else {
          setError('Could not load scores.');
          setIsLoading(false);
        }
      }
    };

    initializeCounters();
  }, [gameStarted, isOnline, playerId, isGuest, getUserDocPath, setDisplayCount, setError, setGlobalCount, setLocalGlobalIncrement, setIsLoading, setOverallMaxCps, setOverallMaxCombo, setSoundEnabled, setWeeklyAchievementData]);
}
